/**
 * i18next configuration for Arabic, Spanish, and English.
 *
 * Architecture:
 * - 9 namespaces: common, landing, search, scan, report, auth, scoring, dashboard, builder
 * - 3 languages: en (default), ar (RTL), es
 * - Fallback chain: target lang → en → key
 * - All resources bundled at build time (no lazy-loading needed at this scale)
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// ── English ───────────────────────────────────────────────────────────────────
import enCommon from './locales/en/common.json'
import enLanding from './locales/en/landing.json'
import enSearch from './locales/en/search.json'
import enScan from './locales/en/scan.json'
import enReport from './locales/en/report.json'
import enAuth from './locales/en/auth.json'
import enScoring from './locales/en/scoring.json'
import enDashboard from './locales/en/dashboard.json'
import enBuilder from './locales/en/builder.json'

// ── Arabic ────────────────────────────────────────────────────────────────────
import arCommon from './locales/ar/common.json'
import arLanding from './locales/ar/landing.json'
import arSearch from './locales/ar/search.json'
import arScan from './locales/ar/scan.json'
import arReport from './locales/ar/report.json'
import arAuth from './locales/ar/auth.json'
import arScoring from './locales/ar/scoring.json'
import arDashboard from './locales/ar/dashboard.json'
import arBuilder from './locales/ar/builder.json'

// ── Spanish ───────────────────────────────────────────────────────────────────
import esCommon from './locales/es/common.json'
import esLanding from './locales/es/landing.json'
import esSearch from './locales/es/search.json'
import esScan from './locales/es/scan.json'
import esReport from './locales/es/report.json'
import esAuth from './locales/es/auth.json'
import esScoring from './locales/es/scoring.json'
import esDashboard from './locales/es/dashboard.json'
import esBuilder from './locales/es/builder.json'

export const SUPPORTED_LANGUAGES = ['en', 'ar', 'es'] as const
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        landing: enLanding,
        search: enSearch,
        scan: enScan,
        report: enReport,
        auth: enAuth,
        scoring: enScoring,
        dashboard: enDashboard,
        builder: enBuilder,
      },
      ar: {
        common: arCommon,
        landing: arLanding,
        search: arSearch,
        scan: arScan,
        report: arReport,
        auth: arAuth,
        scoring: arScoring,
        dashboard: arDashboard,
        builder: arBuilder,
      },
      es: {
        common: esCommon,
        landing: esLanding,
        search: esSearch,
        scan: esScan,
        report: esReport,
        auth: esAuth,
        scoring: esScoring,
        dashboard: esDashboard,
        builder: esBuilder,
      },
    },
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    // Log missing keys to console in dev only
    saveMissing: import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnNull: false,
  })

export default i18n
